import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { stateOutlines } from "../../components/OurLocations";

const planServices = [
  {
    label: "Traffic Management Plans",
    desc: (suburb) =>
      `Professionally prepared TMPs for all project types in ${suburb}, compliant with Transport for NSW and relevant council requirements.`,
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    label: "Traffic Guidance Schemes",
    desc: (suburb) =>
      `Detailed TGS diagrams showing sign placement, delineation and lane configurations for ${suburb} worksites, compliant with AS 1742.3.`,
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    ),
  },
  {
    label: "Council Permit Applications",
    desc: (suburb, council) =>
      `Full preparation and submission of road occupancy licences and council permit applications directly to ${council}.`,
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    label: "Pedestrian Control Plans",
    desc: (suburb) =>
      `Safe pedestrian management routes and signage plans for construction and maintenance projects in ${suburb}.`,
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    label: "Road Closure & Detour Plans",
    desc: (suburb) =>
      `Authority-approved temporary road closure and detour diagrams for works in and around ${suburb}.`,
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
    ),
  },
  {
    label: "Construction Zone Plans",
    desc: (suburb) =>
      `Comprehensive construction traffic management documentation for civil, residential and commercial builds in ${suburb}.`,
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
];

/**
 * Reusable location page template.
 *
 * Props:
 *  suburb      – "Parramatta"
 *  state       – "NSW"
 *  postcode    – "2150"
 *  council     – "City of Parramatta Council"
 *  keyRoads    – ["Church Street", "Parramatta Road", ...]
 *  intro       – paragraph describing the area context
 *  bodyPara    – second paragraph about Focus Traffic Plans' role there
 *  industries  – string[]
 *  nearbySuburbs – string[]
 */
export default function LocationPage({
  suburb,
  state,
  postcode,
  council,
  keyRoads,
  intro,
  bodyPara,
  industries,
  nearbySuburbs,
}) {
  const location = `${suburb}, ${state}`;
  const stateOutline = stateOutlines.find((item) => item.id === state.toLowerCase());
  const pageTitle = `Traffic Management Plans ${suburb} ${state} | Focus Traffic Plans`;
  const metaDesc = `Professional traffic management plan documentation for construction, roadworks, utilities and events in ${suburb} and surrounding ${state} areas. 24-hour turnaround. Call 1300 118 170.`;
  const canonicalUrl = `https://focustrafficplans.com.au/locations/${suburb.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={`traffic management plan ${suburb}, TMP ${suburb}, traffic guidance scheme ${suburb}, council permit ${suburb}, road closure plan ${suburb}, ${suburb} ${state} traffic plans`} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Focus Traffic Plans" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDesc} />

        {/* Local business structured data */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Focus Traffic Plans",
          "description": metaDesc,
          "url": canonicalUrl,
          "telephone": "1300118170",
          "email": "info@focustrafficplans.com.au",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite 4.04, 5 Celebration Drive",
            "addressLocality": "Bella Vista",
            "addressRegion": "NSW",
            "postalCode": "2153",
            "addressCountry": "AU",
          },
          "areaServed": {
            "@type": "City",
            "name": suburb,
            "containedInPlace": {
              "@type": "State",
              "name": state === "NSW" ? "New South Wales" : state,
            },
          },
          "serviceType": [
            "Traffic Management Plans",
            "Traffic Guidance Schemes",
            "Council Permit Applications",
            "Pedestrian Control Plans",
            "Road Closure Plans",
          ],
        })}</script>
      </Helmet>
      {/* Hero */}
      <div className="border-b border-gray-100 bg-gray-50 px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          {/* Breadcrumb */}
          <nav className="mb-6 flex items-center gap-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-orange-500 transition-colors">Home</Link>
            <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <Link to="/#locations" className="hover:text-orange-500 transition-colors">Service Areas</Link>
            <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="font-medium text-orange-500">{location}</span>
          </nav>

          <div className="max-w-3xl">
            <div className="flex items-center gap-4 sm:gap-6">
              {stateOutline && (
                <svg
                  viewBox="0 0 100 100"
                  className="h-16 w-16 shrink-0 text-orange-500 sm:h-20 sm:w-20 lg:h-24 lg:w-24"
                  aria-hidden
                >
                  <path d={stateOutline.d} fill="currentColor" />
                </svg>
              )}
              <h1 className="text-4xl font-extrabold uppercase tracking-tight text-black sm:text-5xl lg:text-6xl">
                Traffic Management Plans
                <br />
                <span className="text-orange-500">{suburb}, {state}</span>
              </h1>
            </div>
            <p className="mt-5 text-[15px] leading-relaxed text-gray-500 max-w-xl">
              Professional traffic management plan documentation for construction,
              roadworks, utilities and events in {suburb} and surrounding{" "}
              {state} areas.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-orange-500/30 transition hover:bg-orange-600 active:scale-[0.98]"
              >
                Get a Free Quote
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <a
                href="tel:1300118170"
                className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-6 py-3 text-sm font-bold uppercase tracking-wide text-gray-800 transition hover:border-orange-400 hover:text-orange-500"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Call 1300 118 170
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1fr_320px]">
          {/* Main column */}
          <div>
            {/* Intro */}
            <h2 className="mb-5 text-2xl font-extrabold uppercase tracking-tight text-black">
              Traffic Management Plan Services in {suburb}
            </h2>
            <p className="mb-4 text-[15px] leading-relaxed text-gray-600">{intro}</p>
            <p className="mb-10 text-[15px] leading-relaxed text-gray-600">
              {bodyPara}{" "}
              Focus Traffic Plans works closely with{" "}
              <strong>{council}</strong> and Transport for{" "}
              {state === "NSW" ? "NSW" : state} to ensure every document meets
              all regulatory requirements.
            </p>

            {/* Services grid */}
            <h3 className="mb-5 text-lg font-extrabold uppercase tracking-wide text-orange-500">
              Our Services in {suburb}
            </h3>
            <p className="mb-6 text-[15px] leading-relaxed text-gray-600">
              We offer the complete range of traffic management plan documentation
              for projects in {suburb}, tailored to local road conditions and
              council requirements:
            </p>
            <div className="mb-12 grid gap-4 sm:grid-cols-2">
              {planServices.map((s) => (
                <div
                  key={s.label}
                  className="flex gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-500">
                    {s.icon}
                  </span>
                  <div>
                    <p className="mb-1 font-semibold text-gray-900">{s.label}</p>
                    <p className="text-[13px] leading-snug text-gray-500">
                      {s.desc(suburb, council)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Industries */}
            <h3 className="mb-4 text-lg font-extrabold uppercase tracking-wide text-orange-500">
              Industries We Serve in {suburb}
            </h3>
            <ul className="mb-12 grid gap-2 sm:grid-cols-2">
              {industries.map((ind) => (
                <li key={ind} className="flex items-center gap-2.5 text-[14px] text-gray-700">
                  <svg className="h-4 w-4 shrink-0 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  {ind}
                </li>
              ))}
            </ul>

            {/* Nearby suburbs */}
            <h3 className="mb-4 text-lg font-extrabold uppercase tracking-wide text-orange-500">
              Also Servicing Suburbs Near {suburb}
            </h3>
            <p className="mb-4 text-[15px] text-gray-600">
              Our traffic management plan team regularly works throughout the{" "}
              {suburb} area and surrounding suburbs:
            </p>
            <div className="flex flex-wrap gap-2">
              {nearbySuburbs.map((s) => (
                <Link
                  key={s}
                  to="/contact"
                  className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-orange-400 hover:text-orange-500"
                >
                  {s}
                </Link>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="flex flex-col gap-5">
            {/* Quick facts */}
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
              <h3 className="mb-4 text-base font-bold text-gray-900">
                Quick Facts – {suburb}
              </h3>
              <div className="h-px w-full bg-gray-200 mb-4" />
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-3">
                  <svg className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {suburb}, {state} {postcode}
                </li>
                <li className="flex items-start gap-3">
                  <svg className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  {council}
                </li>
                <li className="flex items-start gap-3">
                  <svg className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  <span>
                    Key roads:{" "}
                    {keyRoads.join(", ")}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  24-hour plan turnaround available
                </li>
                <li className="flex items-start gap-3">
                  <svg className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  Same-day response available
                </li>
              </ul>
            </div>

            {/* Get a quote */}
            <div className="rounded-2xl bg-[#1a1a1a] p-6 text-white">
              <h3 className="mb-1 text-base font-bold">Get a Quote for {suburb}</h3>
              <div className="my-4 h-px w-full bg-white/10" />
              <p className="mb-5 text-sm leading-relaxed text-white/70">
                Tell us about your {suburb} project and we'll respond within 24
                hours with a tailored traffic management plan solution.
              </p>
              <a
                href="tel:1300118170"
                className="mb-3 flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 text-sm font-bold text-white transition hover:bg-orange-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                1300 118 170
              </a>
              <Link
                to="/contact"
                className="flex w-full items-center justify-center rounded-xl border border-white/15 px-4 py-3 text-sm font-bold text-white transition hover:bg-white/10"
              >
                Email Us
              </Link>
            </div>

            {/* Services list */}
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
              <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-gray-900">
                Our Services
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                {planServices.map((s) => (
                  <li key={s.label} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
                    {s.label}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
